import React, { FC } from "react";
import { AppTheme } from "./App";

interface PolarCoordinates {
  x: number;
  y: number;
}

/** Färger som ett högtidstema kan skicka in i stället för temats standard. */
export interface RingColors {
  segmentFrom: string;
  segmentTo: string;
  segmentStroke: string;
  text: string;
}

interface RingProps {
  theme: AppTheme;
  strings: string[];
  cx: number;
  cy: number;
  innerRadius: number;
  outerRadius: number;
  colors?: RingColors;
  /** Unikt prefix för gradient-id:n så att ringarna inte skriver över varandra. */
  idPrefix?: string;
}

export const polarToCartesian = (
  centerX: number,
  centerY: number,
  radius: number,
  angleInDegrees: number
): PolarCoordinates => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;

  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
};

export const describeSegment = (
  x: number,
  y: number,
  innerRadius: number,
  outerRadius: number,
  startAngle: number,
  endAngle: number
): string => {
  const outerStart = polarToCartesian(x, y, outerRadius, endAngle);
  const outerEnd = polarToCartesian(x, y, outerRadius, startAngle);

  const innerStart = polarToCartesian(x, y, innerRadius, endAngle);
  const innerEnd = polarToCartesian(x, y, innerRadius, startAngle);

  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

  const d = [
    "M",
    outerStart.x,
    outerStart.y,
    "A",
    outerRadius,
    outerRadius,
    0,
    largeArcFlag,
    0,
    outerEnd.x,
    outerEnd.y,
    "L",
    innerEnd.x,
    innerEnd.y,
    "A",
    innerRadius,
    innerRadius,
    0,
    largeArcFlag,
    1,
    innerStart.x,
    innerStart.y,
    "Z",
  ].join(" ");

  return d;
};

/** Standardfärger per app-tema. Högtidsteman skickar egna via `colors`. */
export const themeColors = (theme: AppTheme): RingColors => {
  switch (theme) {
    case "bw":
      return { segmentFrom: "#fbf5fe", segmentTo: "#f1e6fa", segmentStroke: "#dcdcdc", text: "#000000" };
    case "fikahjulet":
      /* Samma mjuka toning som i ljust läge – läser som vitt mot den mörka bakgrunden */
      return { segmentFrom: "#fbf5fe", segmentTo: "#f1e6fa", segmentStroke: "#100a1c", text: "#180f26" };
    default:
      return { segmentFrom: "#f0f0f0", segmentTo: "#e0e0e0", segmentStroke: "#b0b0b0", text: "#333333" };
  }
};

const Ring: FC<RingProps> = ({
  theme,
  strings,
  cx,
  cy,
  innerRadius,
  outerRadius,
  colors,
  idPrefix = "grad",
}) => {
  const segmentAngle = 360 / strings.length;
  const c = colors ?? themeColors(theme);

  return (
    <g>
      {strings.map((string, index) => {
        const startAngle = index * segmentAngle;
        const endAngle = (index + 1) * segmentAngle;
        const middleAngle = (startAngle + endAngle) / 2;

        const labelPosition = polarToCartesian(
          cx,
          cy,
          (outerRadius + innerRadius) / 2,
          middleAngle
        );

        const gradientId = `${idPrefix}-${index}`;

        return (
          <g key={index}>
            <defs>
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style={{ stopColor: c.segmentFrom, stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: c.segmentTo, stopOpacity: 1 }} />
              </linearGradient>
            </defs>
            {/* Segment */}
            <path
              d={describeSegment(
                cx,
                cy,
                innerRadius,
                outerRadius,
                startAngle,
                endAngle
              )}
              fill={`url(#${gradientId})`}
              stroke={c.segmentStroke}
              strokeWidth={colors ? 2 : 1.5}
            />

            {/* Label */}
            <text
              x={labelPosition.x}
              y={labelPosition.y}
              fill={c.text}
              fontSize="22"
              fontFamily="'Geist Sans', sans-serif"
              fontWeight={600}
              textAnchor="middle"
              dy=".3em"
              transform={`rotate(${middleAngle} ${labelPosition.x} ${labelPosition.y})`}
            >
              {string}
            </text>
          </g>
        );
      })}
    </g>
  );
};

export default Ring;
